import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type AdminUser, type AppUser } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ConfirmDelete } from "@/components/admin/confirm-delete"

export const Route = createFileRoute("/admin/_authed/users")({
  component: UsersPage,
})

type FormState = {
  name: string
  email: string
  password: string
  is_active: boolean
}
const empty: FormState = { name: "", email: "", password: "", is_active: true }

function UsersPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<AppUser[]>({
    queryKey: ["admin", "users"],
    queryFn: async () => (await api.get<ApiEnvelope<AppUser[]>>("/admin/users")).data.data,
  })
  // Already cached by the /admin/_authed route's beforeLoad — used here only
  // to know which row is "me" so self-deactivate/self-delete can be disabled
  // in the UI (the backend rejects them either way, this is just so the
  // person doesn't hit that error after filling out the whole form).
  const { data: me } = useQuery<AdminUser | null>({
    queryKey: ["auth", "me"],
    queryFn: async () => (await api.get<AdminUser>("/auth/me")).data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<AppUser | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "users"] })

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, string | boolean> = {
        name: form.name,
        email: form.email,
        is_active: form.is_active,
      }
      if (form.password) payload.password = form.password
      if (editing) {
        await api.put(`/admin/users/${editing.id}`, payload)
      } else {
        await api.post("/admin/users", payload)
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "User updated." : "User created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/users/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("User deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (user: AppUser) => {
    setEditing(user)
    setForm({ name: user.name, email: user.email, password: "", is_active: user.is_active })
    setFieldErrors({})
    setOpen(true)
  }

  const isSelf = (user: AppUser) => !!me && me.id === user.id

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Users</h1>
          <p className="mt-1 text-sm text-muted-foreground">Admin accounts that can sign in to this panel.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    aria-invalid={!!fieldErrors.name}
                  />
                  <FieldError message={fieldErrors.name} />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    aria-invalid={!!fieldErrors.email}
                  />
                  <FieldError message={fieldErrors.email} />
                </div>
              </div>
              <div>
                <Label htmlFor="password">{editing ? "New password" : "Password"}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  aria-invalid={!!fieldErrors.password}
                />
                {editing && (
                  <p className="mt-1 text-xs text-muted-foreground">Leave blank to keep their current password.</p>
                )}
                <FieldError message={fieldErrors.password} />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  id="is_active"
                  disabled={!!editing && isSelf(editing)}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              {editing && isSelf(editing) && (
                <p className="text-xs text-muted-foreground">
                  You can't deactivate your own account — a deactivated user is signed out immediately, including
                  you.
                </p>
              )}
              <FieldError message={fieldErrors.is_active} />
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">Couldn't load users.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data ?? []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    {user.name}
                    {isSelf(user) && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.is_active ? "default" : "secondary"}>
                      {user.is_active ? "Active" : "Deactivated"}
                    </Badge>
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(user)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete
                      itemLabel={user.name}
                      onConfirm={() => remove.mutate(user.id)}
                      disabled={isSelf(user)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
